import { useState, useEffect } from 'react';
import {
  Mail, Loader, AlertCircle, Lock, Eye, EyeOff, ArrowLeft, CheckCircle, ArrowRight
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { authService } from '../../services/PlatformService';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1=email, 2=code, 3=new password, 4=success
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [codeCountdown, setCodeCountdown] = useState(0);

  // Countdown timer
  useEffect(() => {
    if (codeCountdown > 0) {
      const timer = setTimeout(() => setCodeCountdown(codeCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [codeCountdown]);

  // Step 1: Enviar código
  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('E-mail é obrigatório');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('E-mail inválido');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await authService.sendPasswordResetCode(email);
      setStep(2);
      setCodeCountdown(60);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao enviar código.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reenviar código
  const handleResendCode = async () => {
    if (codeCountdown > 0) return;
    setIsSubmitting(true);
    setError('');

    try {
      await authService.sendPasswordResetCode(email);
      setCodeCountdown(60);
      setCode('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao reenviar código.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step 2: Verificar código
  const handleVerifyCode = async () => {
    if (code.length !== 6) return;
    setIsSubmitting(true);
    setError('');

    try {
      const response = await authService.verifyPasswordResetCode(email, code);
      setResetToken(response.data.reset_token);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || 'Código inválido.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step 3: Redefinir senha
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      setError('Senha deve ter pelo menos 8 caracteres');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Senhas não coincidem');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await authService.resetPassword(email, resetToken, newPassword);
      setStep(4);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao redefinir senha.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Títulos e subtítulos por step
  const stepContent = {
    1: { title: 'Esqueci minha senha', subtitle: 'Digite seu e-mail para receber o código de recuperação' },
    2: { title: 'Verifique seu e-mail', subtitle: <>Enviamos um código de 6 dígitos para <strong>{email}</strong></> },
    3: { title: 'Nova senha', subtitle: 'Defina sua nova senha de acesso' },
    4: { title: 'Senha alterada!', subtitle: 'Sua senha foi redefinida com sucesso' },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex flex-col">

      {/* Top Bar - Voltar */}
      <div className="w-full px-4 pt-4">
        <div className="max-w-md mx-auto">
          {step === 1 ? (
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar ao login
            </Link>
          ) : step < 4 ? (
            <button
              onClick={() => {
                setStep(step - 1);
                setError('');
                if (step === 2) setCode('');
              }}
              className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar
            </button>
          ) : null}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center py-8 px-4">
        <div className="max-w-md w-full">

          {/* Logo e Header */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-6">
              <img
                src="/logo.png"
                alt="hello, social"
                className="w-24 h-24 object-contain mx-auto"
              />
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {stepContent[step].title}
            </h1>
            <p className="text-base text-gray-600">
              {stepContent[step].subtitle}
            </p>
          </div>

          {/* Card */}
          <div className="bg-white border-2 border-gray-200 rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all">

            {/* Erro */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-start mb-5">
                <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* ===== STEP 1: Email ===== */}
            {step === 1 && (
              <form onSubmit={handleSendCode} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    E-mail
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                      <Mail className="w-4 h-4" />
                    </span>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); if (error) setError(''); }}
                      className="w-full pl-10 pr-4 py-3 text-sm border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-amber-100 focus:border-amber-500 transition-all"
                      placeholder="Digite seu e-mail"
                      autoFocus
                      required
                    />
                  </div>
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    className="w-full py-3 px-4 bg-gradient-to-r from-amber-400 to-orange-500 rounded-xl text-white font-semibold hover:from-amber-500 hover:to-orange-600 transition-all transform hover:scale-[1.02] shadow-lg focus:outline-none focus:ring-4 focus:ring-amber-100 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader className="w-5 h-5 mr-2 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        Enviar código
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </button>
                </div>

                <div className="text-center pt-4 border-t border-gray-100">
                  <p className="text-sm text-gray-600">
                    Lembrou a senha?{' '}
                    <Link
                      to="/login"
                      className="font-semibold text-amber-600 hover:text-amber-700 transition-colors hover:underline"
                    >
                      Fazer login
                    </Link>
                  </p>
                </div>
              </form>
            )}

            {/* ===== STEP 2: Código ===== */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-amber-50 rounded-2xl mb-4">
                    <Mail className="w-7 h-7 text-amber-500" />
                  </div>
                  <p className="text-sm text-gray-600">
                    Digite o código de 6 dígitos que enviamos para o seu e-mail
                  </p>
                </div>

                <div>
                  <input
                    type="text"
                    maxLength={6}
                    value={code}
                    onChange={(e) => {
                      setCode(e.target.value.replace(/\D/g, ''));
                      if (error) setError('');
                    }}
                    className="w-full text-center text-2xl tracking-[0.5em] py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-amber-100 focus:border-amber-500 transition-all font-mono"
                    placeholder="000000"
                    autoFocus
                  />
                </div>

                <button
                  onClick={handleVerifyCode}
                  disabled={code.length !== 6 || isSubmitting}
                  className="w-full py-3 px-4 bg-gradient-to-r from-amber-400 to-orange-500 rounded-xl text-white font-semibold hover:from-amber-500 hover:to-orange-600 transition-all transform hover:scale-[1.02] shadow-lg focus:outline-none focus:ring-4 focus:ring-amber-100 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader className="w-5 h-5 mr-2 animate-spin" />
                      Verificando...
                    </>
                  ) : (
                    <>
                      Verificar código
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </button>

                <div className="text-center text-sm">
                  {codeCountdown > 0 ? (
                    <span className="text-gray-500">
                      Reenviar código em <strong>{codeCountdown}s</strong>
                    </span>
                  ) : (
                    <button
                      onClick={handleResendCode}
                      disabled={isSubmitting}
                      className="text-amber-600 hover:text-amber-700 font-medium hover:underline transition-colors"
                    >
                      {isSubmitting ? 'Enviando...' : 'Reenviar código'}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* ===== STEP 3: Nova senha ===== */}
            {step === 3 && (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Nova senha
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                      <Lock className="w-4 h-4" />
                    </span>
                    <input
                      type={showPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => { setNewPassword(e.target.value); if (error) setError(''); }}
                      className="w-full pl-10 pr-10 py-3 text-sm border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-amber-100 focus:border-amber-500 transition-all"
                      placeholder="Mínimo 8 caracteres"
                      autoFocus
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 transition-colors"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Confirme a nova senha
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                      <Lock className="w-4 h-4" />
                    </span>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => { setConfirmPassword(e.target.value); if (error) setError(''); }}
                      className="w-full pl-10 pr-10 py-3 text-sm border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-amber-100 focus:border-amber-500 transition-all"
                      placeholder="Digite a senha novamente"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 transition-colors"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    className="w-full py-3 px-4 bg-gradient-to-r from-amber-400 to-orange-500 rounded-xl text-white font-semibold hover:from-amber-500 hover:to-orange-600 transition-all transform hover:scale-[1.02] shadow-lg focus:outline-none focus:ring-4 focus:ring-amber-100 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader className="w-5 h-5 mr-2 animate-spin" />
                        Redefinindo...
                      </>
                    ) : (
                      'Redefinir senha'
                    )}
                  </button>
                </div>
              </form>
            )}

            {/* ===== STEP 4: Sucesso ===== */}
            {step === 4 && (
              <div className="text-center space-y-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-50 rounded-2xl">
                  <CheckCircle className="w-9 h-9 text-green-500" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">
                    Sua senha foi redefinida com sucesso. Agora você pode fazer login com a nova senha.
                  </p>
                </div>
                <Link
                  to="/login"
                  className="inline-block w-full py-3 px-4 bg-gradient-to-r from-amber-400 to-orange-500 rounded-xl text-white font-semibold hover:from-amber-500 hover:to-orange-600 transition-all transform hover:scale-[1.02] shadow-lg text-center"
                >
                  Fazer login
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
